import { useMemo, useState } from "react";
import {
  ShieldCheck,
  CircleDot,
  Clock,
  Lock,
  Send,
  RefreshCw,
  AlertTriangle,
  Check,
  ArrowRight,
} from "lucide-react";
import "./ghg-verification-lovable.css";

const fmt4 = (n) => Number(n || 0).toFixed(4);
const fmt3 = (n) => Number(n || 0).toFixed(3);
const fmtDkk = (n) => Math.round(Number(n || 0)).toLocaleString("da-DK");

const ScopeBadge = ({ scope }) => (
  <span className={`company-ghg-yfs__ghg-scope company-ghg-yfs__ghg-scope--${scope}`}>
    S{scope}
  </span>
);

const ColLabel = ({ children }) => (
  <div className="company-ghg-yfs__ghg-col-label">{children}</div>
);

const Row = ({ label, value, valueColor }) => (
  <div className="company-ghg-yfs__ghg-row">
    <span className="company-ghg-yfs__ghg-row-label">{label}</span>
    <span
      className="company-ghg-yfs__ghg-row-value"
      style={valueColor ? { color: valueColor } : undefined}
    >
      {value}
    </span>
  </div>
);

const StatusPill = ({ status }) => {
  if (status === "locked") {
    return (
      <span className="company-ghg-yfs__ghg-status company-ghg-yfs__ghg-status--locked">
        <Lock size={14} /> Verificeret — låst
      </span>
    );
  }
  if (status === "submitted") {
    return (
      <span className="company-ghg-yfs__ghg-status company-ghg-yfs__ghg-status--submitted">
        <Clock size={14} /> Indsendt — afventer
      </span>
    );
  }
  return (
    <span className="company-ghg-yfs__ghg-status company-ghg-yfs__ghg-status--open">
      <CircleDot size={14} /> Åben — ikke indsendt
    </span>
  );
};

const Field = ({
  label,
  type = "text",
  value,
  onChange,
  mono,
  fullWidth,
  readOnly,
  placeholder,
}) => (
  <div className={fullWidth ? "company-ghg-yfs__ghg-field company-ghg-yfs__ghg-field--full" : "company-ghg-yfs__ghg-field"}>
    <label className="company-ghg-yfs__ghg-field-label">{label}</label>
    {readOnly ? (
      <div className={`company-ghg-yfs__ghg-field-readonly${mono ? " company-ghg-yfs__ghg-field-readonly--mono" : ""}`}>
        {value || <span className="company-ghg-yfs__ghg-field-empty">—</span>}
      </div>
    ) : (
      <input
        type={type}
        value={value}
        placeholder={placeholder}
        onChange={(e) => onChange?.(e.target.value)}
        className={`company-ghg-yfs__ghg-field-input${mono ? " company-ghg-yfs__ghg-field-input--mono" : ""}`}
      />
    )}
  </div>
);

const StepNode = ({ n, state }) => (
  <div className={`company-ghg-yfs__ghg-step-node company-ghg-yfs__ghg-step-node--${state}`}>
    {state === "done" ? <Check size={16} /> : n}
  </div>
);

const GhgVerificationLovable = ({ companyData, facilityName, yearData }) => {
  const [status, setStatus] = useState("open");
  const [internalReviewer, setInternalReviewer] = useState("");
  const [internalReviewDate, setInternalReviewDate] = useState("");
  const [internalNotes, setInternalNotes] = useState("");
  const [step1Saved, setStep1Saved] = useState(false);
  const [editStep1, setEditStep1] = useState(false);
  const [verifierName, setVerifierName] = useState("");
  const [verifierOrg, setVerifierOrg] = useState("");
  const [submittedDate, setSubmittedDate] = useState("");
  const [verificationNumber, setVerificationNumber] = useState("");
  const [verifiedBy, setVerifiedBy] = useState("");
  const [validUntil, setValidUntil] = useState("");

  const year =
    yearData ||
    new URLSearchParams(window.location.search).get("year") ||
    new Date().getFullYear();

  const facility = useMemo(() => {
    const fte = Number(companyData?.total_employees) || 0;
    return {
      name: facilityName || companyData?.business_name || "—",
      fte,
      area_m2: 0,
      revenue_dkk: 0,
      consolidation_approach: "Operational control",
      emission_factor_year: 2024,
      emission_factor_country: "DK",
    };
  }, [companyData, facilityName]);

  const data = useMemo(() => {
    const scope1 = Number(companyData?.scope_1) || 0;
    const scope2loc = Number(companyData?.scope_2) || 0;
    const scope3 = Number(companyData?.scope_3) || 0;
    const totalEmissions = Number(companyData?.total_business_co2) || scope1 + scope2loc + scope3;
    const totalExpenses = 0;
    const rowsWithData = [scope1, scope2loc, scope3].filter((v) => v > 0).length;
    const rowsMissingPrice = 0;
    const rowsNeedingReview = 0;
    const rowsReady = rowsWithData;

    return {
      totalEmissions,
      scope1,
      scope2loc,
      scope3,
      totalExpenses,
      rowsWithData,
      rowsMissingPrice,
      rowsNeedingReview,
      rowsReady,
      missingPriceFlows: [],
      needingReviewFlows: [],
      intensityPerFTE: facility.fte ? totalEmissions / facility.fte : 0,
      intensityPerM2: 0,
      intensityPerMDKK: 0,
    };
  }, [companyData, facility.fte]);

  const step1Complete = step1Saved && internalReviewer && internalReviewDate;
  const step2Complete = !!(verifierName && submittedDate) && status !== "open";
  const step3Complete = status === "locked";

  const issueCount =
    (data.rowsMissingPrice > 0 ? 1 : 0) + (data.rowsNeedingReview > 0 ? 1 : 0);

  const canSubmit =
    !!step1Complete &&
    data.rowsMissingPrice === 0 &&
    data.rowsNeedingReview === 0 &&
    status === "open";

  const noteText =
    issueCount > 0
      ? `Løs de ${issueCount} markerede punkter ovenfor — derefter låses "Indsend" op.`
      : step1Complete
        ? "Intern review gemt — klar til indsendelse."
        : "Udfyld intern review for at fortsætte.";

  const lockedInfo = {
    by: verifiedBy || "Bureau Veritas",
    date: validUntil ? new Date().toLocaleDateString("da-DK") : "—",
    number: verificationNumber || "—",
    until: validUntil || "—",
  };

  const resetVerification = () => {
    setStatus("open");
    setStep1Saved(false);
    setInternalReviewer("");
    setInternalReviewDate("");
    setInternalNotes("");
    setVerifierName("");
    setVerifierOrg("");
    setSubmittedDate("");
    setVerificationNumber("");
    setVerifiedBy("");
    setValidUntil("");
    setEditStep1(false);
  };

  return (
    <section id="ghg-verification" className="company-ghg-yfs__ghg-verif">
      <div className="company-ghg-yfs__ghg-verif-kicker">GHG Verification</div>

      <div className="company-ghg-yfs__ghg-verif-card">
        <div className="company-ghg-yfs__ghg-verif-header">
          <div className="company-ghg-yfs__ghg-verif-header-left">
            <span className="company-ghg-yfs__ghg-verif-icon">
              <ShieldCheck size={16} />
            </span>
            <div>
              <div className="company-ghg-yfs__ghg-verif-title">GHG Verification</div>
              <div className="company-ghg-yfs__ghg-verif-subtitle">
                ISO 14064-3 · GHG Protocol Corporate Standard · {facility.name} · Regnskabsår {year}
              </div>
            </div>
          </div>
          <StatusPill status={status} />
        </div>

        {status === "locked" && (
          <div className="company-ghg-yfs__ghg-locked-banner">
            <Lock size={16} />
            <div>
              Dette regnskab er verificeret og låst. Verificeret af {lockedInfo.by} den {lockedInfo.date} · Verifikationsnr. {lockedInfo.number} · Gyldig til {lockedInfo.until}.
            </div>
          </div>
        )}

        <div className="company-ghg-yfs__ghg-metrics">
          <div className="company-ghg-yfs__ghg-metric-col">
            <ColLabel>Total emissioner</ColLabel>
            <div className="company-ghg-yfs__ghg-metric-main">
              <span className="company-ghg-yfs__ghg-metric-value">{fmt4(data.totalEmissions)}</span>
              <span className="company-ghg-yfs__ghg-metric-unit">tCO₂e</span>
            </div>
            {data.scope1 > 0 && (
              <Row
                label={
                  <span className="company-ghg-yfs__ghg-row-label-inline">
                    Scope 1 <ScopeBadge scope="1" />
                  </span>
                }
                value={fmt4(data.scope1)}
              />
            )}
            {data.scope2loc > 0 && (
              <Row
                label={
                  <span className="company-ghg-yfs__ghg-row-label-inline">
                    Scope 2 loc. <ScopeBadge scope="2" />
                  </span>
                }
                value={fmt4(data.scope2loc)}
              />
            )}
            {data.scope3 > 0 && (
              <Row
                label={
                  <span className="company-ghg-yfs__ghg-row-label-inline">
                    Scope 3 <ScopeBadge scope="3" />
                  </span>
                }
                value={fmt4(data.scope3)}
              />
            )}
          </div>

          <div className="company-ghg-yfs__ghg-metric-col">
            <ColLabel>Total udgifter</ColLabel>
            <div className="company-ghg-yfs__ghg-metric-main">
              <span className="company-ghg-yfs__ghg-metric-value">{fmtDkk(data.totalExpenses)}</span>
              <span className="company-ghg-yfs__ghg-metric-unit">DKK</span>
            </div>
            <div className="company-ghg-yfs__ghg-intensity-label">Intensitet</div>
            {facility.fte > 0 && (
              <Row label="per FTE" value={`${fmt3(data.intensityPerFTE)} tCO₂e`} />
            )}
          </div>

          <div className="company-ghg-yfs__ghg-metric-col">
            <ColLabel>Datagrundlag</ColLabel>
            <Row label="Linjer med data" value={data.rowsWithData} />
            <Row
              label="Mangler pris"
              value={data.rowsMissingPrice}
              valueColor={data.rowsMissingPrice > 0 ? "#BA7517" : undefined}
            />
            <Row
              label="Kræver review"
              value={data.rowsNeedingReview}
              valueColor={data.rowsNeedingReview > 0 ? "#A32D2D" : undefined}
            />
            <Row label="Klar" value={data.rowsReady} valueColor="#085041" />
          </div>

          <div className="company-ghg-yfs__ghg-metric-col company-ghg-yfs__ghg-metric-col--last">
            <ColLabel>Metode</ColLabel>
            <Row label="Standard" value="GHG Protocol" />
            <Row label="Konsolidering" value={facility.consolidation_approach} />
            <Row label="Scope 2" value="Dual reporting" />
            <Row
              label="EF-år · land"
              value={`${facility.emission_factor_year} · ${facility.emission_factor_country}`}
            />
          </div>
        </div>

        <div className="company-ghg-yfs__ghg-steps">
          <div className="company-ghg-yfs__ghg-steps-title">
            Verifikationsproces — udfyld i rækkefølge
          </div>

          <div className="company-ghg-yfs__ghg-step">
            <div className="company-ghg-yfs__ghg-step-rail">
              <StepNode n={1} state={step1Complete && !editStep1 ? "done" : "active"} />
              <div
                className={`company-ghg-yfs__ghg-step-line${step1Complete ? " company-ghg-yfs__ghg-step-line--done" : ""}`}
              />
            </div>
            <div className="company-ghg-yfs__ghg-step-body">
              <div className="company-ghg-yfs__ghg-step-heading">Intern review</div>
              {step1Complete && !editStep1 ? (
                <div className="company-ghg-yfs__ghg-step-grid">
                  <Field label="Godkendt af" value={internalReviewer} readOnly />
                  <Field label="Dato" value={internalReviewDate} readOnly />
                  <Field label="Bemærkninger" value={internalNotes} fullWidth readOnly />
                  <button
                    type="button"
                    onClick={() => setEditStep1(true)}
                    className="company-ghg-yfs__ghg-link-btn"
                  >
                    Rediger
                  </button>
                </div>
              ) : (
                <>
                  <div className="company-ghg-yfs__ghg-step-grid">
                    <Field
                      label="Godkendt af"
                      value={internalReviewer}
                      onChange={setInternalReviewer}
                      placeholder="Navn"
                    />
                    <Field
                      label="Dato"
                      type="date"
                      value={internalReviewDate}
                      onChange={setInternalReviewDate}
                    />
                    <Field
                      label="Bemærkninger"
                      value={internalNotes}
                      onChange={setInternalNotes}
                      fullWidth
                      placeholder="Valgfri"
                    />
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      if (internalReviewer && internalReviewDate) {
                        setStep1Saved(true);
                        setEditStep1(false);
                      }
                    }}
                    disabled={!internalReviewer || !internalReviewDate}
                    className="company-ghg-yfs__ghg-primary-btn"
                  >
                    Gem intern review <ArrowRight size={14} />
                  </button>
                </>
              )}
            </div>
          </div>

          <div className="company-ghg-yfs__ghg-step">
            <div className="company-ghg-yfs__ghg-step-rail">
              <StepNode
                n={2}
                state={
                  step2Complete ? "done" : step1Complete && status === "open" ? "active" : "idle"
                }
              />
              <div
                className={`company-ghg-yfs__ghg-step-line${step2Complete ? " company-ghg-yfs__ghg-step-line--done" : ""}`}
              />
            </div>
            <div className="company-ghg-yfs__ghg-step-body">
              <div
                className={`company-ghg-yfs__ghg-step-heading${!step1Complete ? " company-ghg-yfs__ghg-step-heading--muted" : ""}`}
              >
                Indsend til verifikation
              </div>
              {!step1Complete ? (
                <div className="company-ghg-yfs__ghg-step-locked">
                  Låst — gennemfør intern review først.
                </div>
              ) : status === "open" ? (
                <>
                  <div className="company-ghg-yfs__ghg-step-grid">
                    <Field
                      label="Verifikator"
                      value={verifierName}
                      onChange={setVerifierName}
                      placeholder="Navn"
                    />
                    <Field
                      label="Organisation"
                      value={verifierOrg}
                      onChange={setVerifierOrg}
                      placeholder="Bureau Veritas"
                    />
                    <Field
                      label="Indsendt dato"
                      type="date"
                      value={submittedDate}
                      onChange={setSubmittedDate}
                    />
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      if (canSubmit && verifierName && submittedDate) setStatus("submitted");
                    }}
                    disabled={!canSubmit || !verifierName || !submittedDate}
                    className="company-ghg-yfs__ghg-primary-btn"
                    title={!canSubmit ? "Løs markerede punkter inden indsendelse" : undefined}
                  >
                    Indsend <Send size={14} />
                  </button>
                </>
              ) : (
                <div className="company-ghg-yfs__ghg-step-grid">
                  <Field label="Verifikator" value={verifierName} readOnly />
                  <Field label="Organisation" value={verifierOrg} readOnly />
                  <Field label="Indsendt dato" value={submittedDate} readOnly />
                </div>
              )}
            </div>
          </div>

          <div className="company-ghg-yfs__ghg-step company-ghg-yfs__ghg-step--last">
            <div className="company-ghg-yfs__ghg-step-rail">
              <StepNode
                n={3}
                state={step3Complete ? "done" : status === "submitted" ? "active" : "idle"}
              />
            </div>
            <div className="company-ghg-yfs__ghg-step-body">
              <div
                className={`company-ghg-yfs__ghg-step-heading${status === "open" ? " company-ghg-yfs__ghg-step-heading--muted" : ""}`}
              >
                Verificeret og låst
              </div>
              {status === "open" ? (
                <div className="company-ghg-yfs__ghg-step-locked">Låst — afventer indsendelse.</div>
              ) : status === "submitted" ? (
                <>
                  <div className="company-ghg-yfs__ghg-step-grid">
                    <Field
                      label="Verifikationsnummer"
                      value={verificationNumber}
                      onChange={setVerificationNumber}
                      mono
                      placeholder="BV-DK-2025-…"
                    />
                    <Field label="Verificeret af" value={verifiedBy} onChange={setVerifiedBy} />
                    <Field
                      label="Gyldig til"
                      type="date"
                      value={validUntil}
                      onChange={setValidUntil}
                    />
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      if (verificationNumber && verifiedBy && validUntil) setStatus("locked");
                    }}
                    disabled={!verificationNumber || !verifiedBy || !validUntil}
                    className="company-ghg-yfs__ghg-lock-btn"
                  >
                    Lås regnskabsår <Lock size={14} />
                  </button>
                </>
              ) : (
                <div className="company-ghg-yfs__ghg-step-grid">
                  <Field label="Verifikationsnummer" value={verificationNumber} readOnly mono />
                  <Field label="Verificeret af" value={verifiedBy} readOnly />
                  <Field label="Gyldig til" value={validUntil} readOnly />
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="company-ghg-yfs__ghg-footer">
          <div className="company-ghg-yfs__ghg-footer-note">
            {status === "locked"
              ? "Regnskabsåret er låst. Opret en revision for at foretage ændringer."
              : noteText}
          </div>
          <div className="company-ghg-yfs__ghg-footer-actions">
            {status === "locked" ? (
              <>
                <button type="button" className="company-ghg-yfs__ghg-outline-btn">
                  Vis certifikat
                </button>
                <button type="button" className="company-ghg-yfs__ghg-outline-btn">
                  Eksporter PDF
                </button>
                <button
                  type="button"
                  onClick={resetVerification}
                  className="company-ghg-yfs__ghg-revision-btn"
                >
                  <RefreshCw size={14} /> Opret revision
                </button>
              </>
            ) : (
              <>
                <button type="button" className="company-ghg-yfs__ghg-outline-btn">
                  Eksporter rapport
                </button>
                <button
                  type="button"
                  disabled
                  title="Deaktiveret for indeværende"
                  className="company-ghg-yfs__ghg-primary-btn company-ghg-yfs__ghg-primary-btn--disabled"
                >
                  <Send size={14} /> Indsend til verifikation
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default GhgVerificationLovable;
